import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "No se pudo iniciar sesión"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[480px] lg:flex-none">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10">
            <img src="/logo.png" alt="SoloSoporte" className="h-14 w-auto object-contain" data-testid="login-logo" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">
            Iniciar sesión
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Acceda con su usuario de técnico o administrador.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-slate-500">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@solosoporte.com"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-slate-500">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11"
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="h-11 w-full bg-primary font-semibold hover:bg-[#0353E9] transition-colors duration-200"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Ingresando...
                </span>
              ) : (
                "Ingresar"
              )}
            </Button>
          </form>
        </div>
      </div>
      <div className="relative hidden flex-1 lg:block">
        <img
          src="https://images.unsplash.com/photo-1785682117028-6fcf2c0b515b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxJVCUyMHNlcnZlciUyMHJvb20lMjB0ZWNobmljaWFufGVufDB8fHx8MTc4ODIyNDE2OXww&ixlib=rb-4.1.0&q=85"
          alt="Centro de datos"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F62FE]/70" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-3xl font-black leading-tight">
            Gestión de órdenes de servicio para equipos técnicos.
          </p>
          <p className="mt-3 text-sm text-blue-100">
            Clientes, tickets, materiales, evidencias y reportes en una sola plataforma.
          </p>
        </div>
      </div>
    </div>
  );
}
